import _ from 'lodash'
import Discount from 'src/models/discount/Discount'
import Item from 'src/models/item/Item'
import ItemClass from 'src/models/item/ItemClass'
import MembershipTier from 'src/models/membership-type/MembershipTier'
import MembershipType from 'src/models/membership-type/MembershipType'
import Model, { encoders, ID, Integer, JsonObject, MODEL_LEVEL } from 'src/models/Model'
import Movie from 'src/models/movie/Movie'
import TitleClass from 'src/models/movie/TitleClass'
import ItemPackage from 'src/models/package/ItemPackage'
import ShowingBadge from 'src/models/showing/ShowingBadge'
import Site from 'src/models/site/Site'
import VoucherType from 'src/models/voucher/VoucherType'

export const POINTS_TYPES = {
  BIRTHDAY: 'birthday',
  VISIT: 'visit',
  TICKET_PURCHASE: 'ticket-purchase',
  TICKET_PRICE: 'ticket-price',
  TICKET_VALUE: 'ticket-value',
  CONCESSION_PRICE: 'concession-price',
  MEMBERSHIP_PURCHASE: 'membership-purchase',
  GIFT_CARD_PURCHASE: 'gift-card-purchase',
  GIFT_CARD_PRICE: 'gift-card-price'
}
POINTS_TYPES.ALL = _.values(POINTS_TYPES)

export const MEMBERSHIP_POINT_EXPIRY_TERMS = {
  MONTHS: 'months',
  YEARS: 'years'
}
MEMBERSHIP_POINT_EXPIRY_TERMS.ALL = _.values(MEMBERSHIP_POINT_EXPIRY_TERMS)

export const CONDITIONS = {
  BIRTHDAY: 'birthday',
  BIRTHWEEK: 'birthweek',
  DATE_RANGE: 'date-range',
  DAY_OF_WEEK: 'day-of-week',
  DISCOUNTS: 'discounts',
  DATES: 'dates',
  ITEMS: 'items',
  ITEM_CLASSES: 'item-classes',
  ITEM_PACKAGES: 'item-packages',
  LIFETIME: 'lifetime',
  LONGEVITY: 'longevity',
  MOVIES: 'movies',
  PRICE: 'price',
  SHOWING_BADGES: 'showing-badges',
  SHOWING_BIRTHDAY: 'showing-birthday',
  SHOWING_BIRTHWEEK: 'showing-birthweek',
  SHOWING_DATES: 'showing-dates',
  SHOWING_DATE_RANGE: 'showing-date-range',
  SHOWING_DAY_OF_WEEK: 'showing-day-of-week',
  SHOWING_TIME_OF_DAY: 'showing-time-of-day',
  SITES: 'sites',
  TIER: 'tier',
  TIME_OF_DAY: 'time-of-day',
  TITLE_CLASSES: 'title-classes',
  VOUCHERS: 'vouchers'
}
CONDITIONS.ALL = _.values(CONDITIONS)

CONDITIONS.SHOWING_CONDITIONS = [
  CONDITIONS.MOVIES,
  CONDITIONS.SHOWING_BIRTHDAY,
  CONDITIONS.SHOWING_BIRTHWEEK,
  CONDITIONS.SHOWING_DATES,
  CONDITIONS.SHOWING_DATE_RANGE,
  CONDITIONS.SHOWING_DAY_OF_WEEK,
  CONDITIONS.SHOWING_TIME_OF_DAY,
  CONDITIONS.TITLE_CLASSES
]
CONDITIONS.ITEM_CONDTIONS = [CONDITIONS.ITEMS, CONDITIONS.ITEM_CLASSES, CONDITIONS.ITEM_PACKAGES]

CONDITIONS[POINTS_TYPES.BIRTHDAY] = [CONDITIONS.TIER, CONDITIONS.LONGEVITY, CONDITIONS.DATE_RANGE]
CONDITIONS[POINTS_TYPES.VISIT] = _.without(CONDITIONS.ALL, ...CONDITIONS.ITEM_CONDTIONS)
CONDITIONS[POINTS_TYPES.TICKET_PURCHASE] = _.without(CONDITIONS.ALL, ...CONDITIONS.ITEM_CONDTIONS)
CONDITIONS[POINTS_TYPES.TICKET_PRICE] = _.without(CONDITIONS.ALL, ...CONDITIONS.ITEM_CONDTIONS)
CONDITIONS[POINTS_TYPES.TICKET_VALUE] = _.without(CONDITIONS.ALL, ...CONDITIONS.ITEM_CONDTIONS)
CONDITIONS[POINTS_TYPES.CONCESSION_PRICE] = _.without(CONDITIONS.ALL, ...CONDITIONS.SHOWING_CONDITIONS)
CONDITIONS[POINTS_TYPES.MEMBERSHIP_PURCHASE] = _.without(CONDITIONS.ALL, ...CONDITIONS.ITEM_CONDTIONS, ...CONDITIONS.SHOWING_CONDITIONS)
CONDITIONS[POINTS_TYPES.GIFT_CARD_PURCHASE] = _.without(CONDITIONS.ALL, ...CONDITIONS.ITEM_CONDTIONS, ...CONDITIONS.SHOWING_CONDITIONS, CONDITIONS.VOUCHERS)
CONDITIONS[POINTS_TYPES.GIFT_CARD_PRICE] = _.without(CONDITIONS.ALL, ...CONDITIONS.ITEM_CONDTIONS, ...CONDITIONS.SHOWING_CONDITIONS, CONDITIONS.VOUCHERS)

export const CONDITION_REQUIREMENTS = {
  // DATE_RANGE
  DATE_RANGE_START: 'dateRangeStart',
  DATE_RANGE_END: 'dateRangeEnd',
  EXCLUDE_DATE_RANGE: 'excludeDateRange',
  // DAY_OF_WEEK
  DAY_OF_WEEK: 'dayOfWeek',
  EXCLUDE_DAY_OF_WEEK: 'excludeDayOfWeek',
  // DISCOUNTS
  DISCOUNTS: 'discountIds',
  EXCLUDE_DISCOUNTS: 'excludeDiscounts',
  // DATES
  DATES: 'dates',
  EXCLUDE_DATES: 'excludeDates',
  // ITEMS
  ITEMS: 'siteItemIds',
  EXCLUDE_ITEMS: 'excludeItems',
  // ITEM_CLASSES
  ITEM_CLASSES: 'itemClassIds',
  EXCLUDE_ITEM_CLASSES: 'excludeItemClasses',
  // ITEM_PACKAGES
  ITEM_PACKAGES: 'itemPackageIds',
  EXCLUDE_ITEM_PACKAGES: 'excludeItemPackages',
  // LIFETIME
  LIFETIME_MINIMUM: 'lifetimeMinimum',
  LIFETIME_MAXIMUM: 'lifetimeMaximum',
  LIFETIME_TERM: 'lifetimeTerm',
  // LONGEVITY
  LONGEVITY_MINIMUM: 'longevityMinimum',
  LONGEVITY_MAXIMUM: 'longevityMaximum',
  LONGEVITY_TERM: 'longevityTerm',
  // MOVIES
  MOVIES: 'siteMovieIds',
  EXCLUDE_MOVIES: 'excludeMovies',
  // PRICE
  MIN_PRICE: 'minPrice',
  MAX_PRICE: 'maxPrice',
  // SHOWING_BADGES
  SHOWING_BADGES: 'showingBadgeIds',
  EXCLUDE_SHOWING_BADGES: 'excludeShowingBadges',
  // SHOWING_DATES
  SHOWING_DATES: 'showingDates',
  EXCLUDE_SHOWING_DATES: 'excludeShowingDates',
  // SHOWING_DATE_RANGE
  SHOWING_DATE_RANGE_START: 'showingDateRangeStart',
  SHOWING_DATE_RANGE_END: 'showingDateRangeEnd',
  EXCLUDE_SHOWING_DATE_RANGE: 'excludeShowingDateRange',
  // SHOWING_DAY_OF_WEEK
  SHOWING_DAY_OF_WEEK: 'showingDayOfWeek',
  EXCLUDE_SHOWING_DAY_OF_WEEK: 'excludeShowingDayOfWeek',
  // SHOWING_TIME_OF_DAY
  SHOWING_TIME_OF_DAY_START: 'showingTimeOfDayStart',
  SHOWING_TIME_OF_DAY_END: 'showingTimeOfDayEnd',
  EXCLUDE_SHOWING_TIME_OF_DAY: 'excludeShowingTimeOfDay',
  // SITES
  SITES: 'siteIds',
  EXCLUDE_SITES: 'excludeSites',
  // TITLE_CLASSES
  TIER: 'membershipTierIds',
  // TIME_OF_DAY
  TIME_OF_DAY_START: 'timeOfDayStart',
  TIME_OF_DAY_END: 'timeOfDayEnd',
  EXCLUDE_TIME_OF_DAY: 'excludeTimeOfDay',
  // TITLE_CLASSES
  TITLE_CLASSES: 'titleClassIds',
  EXCLUDE_TITLE_CLASSES: 'excludeTitleClasses',
  // VOUCHERS
  VOUCHERS: 'voucherTypeIds',
  EXCLUDE_VOUCHERS: 'excludeVouchers'
}
CONDITION_REQUIREMENTS.ALL = _.values(CONDITION_REQUIREMENTS)

export default class MembershipPoint extends Model {
  static name = 'MembershipPoint'

  static deletable = true
  static versioned = true

  static level = MODEL_LEVEL.CIRCUIT

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Site]

  static define () {
    this.fields = {
      id: {
        type: ID
      },
      name: {
        type: String,
        sortable: true
      },
      membershipType: {
        type: MembershipType,
        encode: encoders.hasOne,
        reference: true,
        output: true
      },
      pointsType: {
        type: String,
        default: POINTS_TYPES.TICKET_PURCHASE
      },
      pointsRate: {
        type: Integer,
        default: 1
      },
      expire: {
        type: Boolean,
        default: false
      },
      expires: {
        type: Integer,
        default: 1
      },
      expiryTerm: {
        type: String,
        default: MEMBERSHIP_POINT_EXPIRY_TERMS.YEARS
      },
      conditions: {
        type: JsonObject,
        default: _(CONDITIONS.ALL).keyBy().mapValues(() => false).value()
      },
      conditionsRequirements: {
        type: JsonObject,
        mergeDefault: true,
        default: () => ({
          dates: [],
          excludeDates: false,
          dateRangeStart: null,
          dateRangeEnd: null,
          excludeDateRange: false,
          dayOfWeek: [],
          excludeDayOfWeek: false,
          excludeDiscounts: true,
          excludeItems: false,
          excludeItemClasses: false,
          excludeItemPackages: false,
          excludeShowingBadges: false,
          excludeMovies: false,
          excludeTitleClasses: false,
          excludeVouchers: true,
          excludeSites: false,
          lifetimeMinimum: 0,
          lifetimeMaximum: 1,
          lifetimeTerm: 'months',
          longevityMinimum: 1,
          longevityMaximum: null,
          longevityTerm: 'months',
          minPrice: 0,
          maxPrice: null,
          showingDates: [],
          excludeShowingDates: false,
          showingDateRangeStart: null,
          showingDateRangeEnd: null,
          excludeShowingDateRange: false,
          showingDayOfWeek: [],
          excludeShowingDayOfWeek: false,
          timeOfDayStart: null,
          timeOfDayEnd: null,
          excludeTimeOfDay: false
        })
      },
      sites: {
        type: [Site],
        reference: true
      },
      discounts: {
        type: [Discount],
        reference: true
      },
      items: {
        type: [Item],
        reference: true
      },
      siteItems: {
        type: [Item],
        reference: true
      },
      itemClasses: {
        type: [ItemClass],
        reference: true
      },
      itemPackages: {
        type: [ItemPackage],
        reference: true
      },
      siteItemPackages: {
        type: [ItemPackage],
        reference: true
      },
      membershipTiers: {
        type: [MembershipTier],
        reference: true,
        output: true
      },
      membershipTierIds: {
        type: [ID],
        default: () => ([])
      },
      movies: {
        type: [Movie],
        reference: true,
        output: true
      },
      movieIds: {
        type: [ID],
        default: () => ([])
      },
      siteMovies: {
        type: [Movie],
        reference: true,
        output: true
      },
      siteMovieIds: {
        type: [ID],
        default: () => ([])
      },
      showingBadges: {
        type: [ShowingBadge],
        reference: true
      },
      titleClasses: {
        type: [TitleClass],
        reference: true
      },
      voucherTypes: {
        type: [VoucherType],
        reference: true
      },
      enable: {
        type: Boolean,
        default: true
      }
    }

    this.computed = {
      isActive () {
        return this.enable && this.pointsRate > 0
      },
      isConditional () {
        return _.some(this.conditions, condition => condition === true)
      },
      lifetimeDescription () {
        if (!this.conditions[CONDITIONS.LIFETIME]) return null

        if (this.conditionsRequirements.lifetimeMaximum) {
          return `${this.conditionsRequirements.lifetimeMinimum}-${this.conditionsRequirements.lifetimeMaximum} ${this.conditionsRequirements.lifetimeTerm} of ${this.membershipType?.name}`
        } else {
          return `${this.conditionsRequirements.lifetimeMinimum}+ ${this.conditionsRequirements.lifetimeTerm} of ${this.membershipType?.name}`
        }
      },
      longevityDescription () {
        if (!this.conditions[CONDITIONS.LONGEVITY]) return null

        if (this.conditionsRequirements.longevityMaximum) {
          return `${this.conditionsRequirements.longevityMinimum}-${this.conditionsRequirements.longevityMaximum} ${this.conditionsRequirements.longevityTerm} of ${this.membershipType?.name}`
        } else {
          return `${this.conditionsRequirements.longevityMinimum}+ ${this.conditionsRequirements.longevityTerm} of ${this.membershipType?.name}`
        }
      },
      isBirthday () {
        return this.conditions[CONDITIONS.BIRTHDAY]
      },
      showingBadges () {
        return this.conditions[CONDITIONS.SHOWING_BADGES] ? _.map(this.conditionsRequirements.showing_badges, showingBadgeId => { return _.find(this.configuration.showingBadges.data, { id: showingBadgeId }) }) : null
      },
      dayOfWeek () {
        return this.conditions[CONDITIONS.DAY_OF_WEEK] ? ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][this.conditionsRequirements.dayOfWeek] : null
      },
      tierId () {
        return this.conditions[CONDITIONS.TIER] ? this.conditionsRequirements.tier : null
      },
      expiryDescription () {
        return `${this.expires} ${this.expires === 1 ? this.expiryTerm.slice(0, -1) : this.expiryTerm}`
      },
      formattedConditions () {
        const conditions = _.map(this.$mh.objectToArray(this.conditions), condition => _.startCase(condition))

        if (this.conditions[CONDITIONS.TIER]) {
          const tierNames = _(this.membershipTiers).map('name').compact().value()
          if (tierNames.length) {
            _.remove(conditions, condition => condition === _.startCase(CONDITIONS.TIER))
            conditions.push(tierNames.join(', '))
          }
        }
        if (this.conditions[CONDITIONS.DAY_OF_WEEK]) {
          _.remove(conditions, condition => condition === CONDITIONS.DAY_OF_WEEK)
          conditions.push(`${this.conditionsRequirements.excludeDayOfWeek ? 'Exclude' : 'Include'} ${_.map(this.conditionsRequirements.dayOfWeek, index => ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][index]).join(', ')}`)
        }
        if (this.conditions[CONDITIONS.SHOWING_BADGES]) {
          _.remove(conditions, condition => condition === CONDITIONS.SHOWING_BADGES)

          _.each(this.conditionsRequirements.showingBadges, showingBadgeId => {
            const showingBadge = _.find(this.configuration.showingBadges.data, { id: showingBadgeId })
            if (showingBadge?.displayName) {
              conditions.push(showingBadge.displayName)
            }
          })
        }
        if (this.conditions[CONDITIONS.LIFETIME]) {
          _.remove(conditions, condition => condition === CONDITIONS.LIFETIME)
          if (this.conditionsRequirements.lifetimeMaximum) {
            conditions.push(`${this.conditionsRequirements.lifetimeMinimum}-${this.conditionsRequirements.lifetimeMaximum} ${_.startCase(this.conditionsRequirements.lifetimeTerm).slice(0, -1)} Member`)
          } else {
            conditions.push(`${this.conditionsRequirements.lifetimeMinimum}+ ${_.startCase(this.conditionsRequirements.lifetimeTerm).slice(0, -1)} Member`)
          }
        }
        if (this.conditions[CONDITIONS.LONGEVITY]) {
          _.remove(conditions, condition => condition === CONDITIONS.LONGEVITY)
          if (this.conditionsRequirements.longevityMaximum) {
            conditions.push(`${this.conditionsRequirements.longevityMinimum}-${this.conditionsRequirements.longevityMaximum} ${_.startCase(this.conditionsRequirements.longevityTerm).slice(0, -1)} Member`)
          } else {
            conditions.push(`${this.conditionsRequirements.longevityMinimum}+ ${_.startCase(this.conditionsRequirements.longevityTerm).slice(0, -1)} Member`)
          }
        }
        if (this.conditions[CONDITIONS.DATE_RANGE]) {
          _.remove(conditions, condition => condition === CONDITIONS.DATE_RANGE)
          let customDateCondition = `${this.i18n.d(new Date(this.conditionsRequirements.dateRangeStart), 'shortDate')}`
          if (this.conditionsRequirements.dateRangeEnd) {
            customDateCondition += ` to ${this.i18n.d(new Date(this.conditionsRequirements.dateRangeEnd), 'shortDate')}`
          } else {
            customDateCondition += '+'
          }
          conditions.push(customDateCondition)
        }

        return _(conditions).map(condition => _.startCase(condition)).value()
      }
    }
  }
}
